import React, { useContext, useEffect, useRef } from 'react';
import { Box, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useScreenSize from '../../../utils/useScreenSize';
import { ReaderContext } from '../../Reader/ReaderContext';
import { ImageResolutions } from '@kimuno/api-sdk/lib/types/responses/Common';
import { borderRadius, spaces } from '../../../assets/styles/themes/tokens';
import useRouter from '../../../utils/useRouter';
import ReaderBannerContainer from '../../ReaderBannerContainer';

interface BasisReaderContentProps {
    containerRef: React.RefObject<HTMLDivElement>; 
    pages: ImageResolutions[];
    zoom: number;
    isReadOnly: boolean;
    downloadMindItem: () => void;
}

const getOpacity = (index: number): number => {
  switch (index) {
    case 0:
      return 1;
    case 1:
      return 0.33;
    default:
      return 0.2;
  }
};

const BasisReaderContent: React.FC<BasisReaderContentProps> = (props) => {
  const { pages, zoom, containerRef, isReadOnly, downloadMindItem } = props;
  const { isMdScreen, isLgScreen } = useScreenSize();
  const isDesktopScreen = isLgScreen || isMdScreen;
  const { onPageLoad, setCurrentPage } = useContext(ReaderContext);
  const { t } = useTranslation();
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { match: { location: { query } } } = useRouter();
  const { page } = query;

  const setPageRefs = (index: number) => (el: HTMLDivElement | null) => {
    pageRefs.current[index] = el;
  };

  useEffect(() => {
    if (page) {
      const newPage = parseInt(page, 10);
      const pageRef = pageRefs.current[newPage - 1];
      setCurrentPage(newPage);
      pageRef?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else {
      setCurrentPage(1);
      containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [page, pages.length, setCurrentPage]);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const scrollTop = containerRef.current.scrollTop;
        const pageHeight = containerRef.current.scrollHeight / pages.length;
        const newPage = Math.min(pages.length, Math.max(1, Math.ceil(scrollTop / pageHeight)));
        setCurrentPage(newPage);
      }
    };

    const currentRef = containerRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, [containerRef, pages.length, setCurrentPage]);

  useEffect(() => {
    pageRefs.current.length = pages.length;
  }, [pages.length]);

  return (
    <Box 
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: spaces.md,
        gap: spaces.md,
        height: '100%',
        overflowY: 'scroll',
        ...(!isDesktopScreen && { width: '100%' }),
      }}
      data-testid='reader-basis-content-container'
    >
      {pages.map((page, i) => {   
        const imageAlt = t('Page {{number}}', { number: i + 1 });
        return (
          <Paper
            key={i}
            ref={setPageRefs(i)}
            elevation={0}
            sx={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: `${zoom}%`,
              borderRadius: borderRadius.sm,
              transform: 'scale(1)',
            }}
            data-testid={`basis-reader__page-container-${i}`}
          >
            {i > 0 && (
              <ReaderBannerContainer 
                index={i} 
                isReadOnly={isReadOnly} 
                downloadMindItem={downloadMindItem} 
              />
            )}
            <Box
              component='img'
              src={page.w2480}
              onLoad={onPageLoad}
              alt={imageAlt}
              sx={{
                width: '100%',
                height: 'auto',
                opacity: getOpacity(i),
                display: 'block',
                borderRadius: 'inherit',
              }}
              data-testid={`reader__page-container-${i}__image`}
            />
          </Paper>
        );
      })}
    </Box>
  );
};

export default BasisReaderContent;
